/**
 * /sessions — this season's full schedule, and the ONE place registration is
 * offered if a paid or capacity-limited program is open in the host portal
 * (admin → Programs). Connect itself is free and drop-in, so by default this
 * page is the schedule + poster (or a summary, once there is no poster) +
 * "just turn up".
 *
 * CMS-driven twice over: the schedule/poster come from the `season` block
 * (host portal → Site content) and any program cards from admin → Programs,
 * gated by `canEnrolNow` — the same rule the API enforces. "Register now"
 * uses `useCurrentProgram()`, the same "the latest active, open, unfinished
 * program" rule the Nav and home page use, so the season program (once
 * seeded) drives the CTA without this page needing to know its id.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CalendarDays,
  Clock,
  Coffee,
  GraduationCap,
  MapPin,
  UtensilsCrossed,
} from "lucide-react";
import { canEnrolNow, formatEventDates, hasProgramFinished } from "@alight/contracts";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Section } from "@alight/ui";
import { useSeo } from "../lib/seo";
import { locationNames, locationShort, locationShortList, useBlock } from "../lib/content";
import { useCurrentProgram } from "../lib/current-program";
import { formatRowLabel, groupSessionsByMonth, summariseSeason } from "../lib/season";
import type { Session } from "../lib/season";
import { PageIntro } from "../components/PageIntro";
import { JsonLd } from "../components/JsonLd";
import { eventsJsonLd } from "../lib/structured-data";

export const Route = createFileRoute("/sessions")({
  component: SessionsPage,
});

/** One row of the schedule — a Connect session, Campus Time, or a break. */
function SessionRow({ session, isNext }: { session: Session; isNext: boolean }) {
  const label = formatRowLabel(session.date) || session.date;

  if (session.kind === "break") {
    return (
      <li className="flex items-center gap-3 rounded-card border border-dashed border-border bg-transparent px-4 py-3 text-muted">
        <Coffee className="h-4 w-4 shrink-0" aria-hidden />
        <span className="font-semibold">{label}</span>
        <span className="text-sm">— No Connect — break</span>
      </li>
    );
  }

  const campus = session.kind === "campus";
  return (
    <li
      className={`rounded-card border px-4 py-3 ${
        isNext
          ? "border-primary bg-primary/5 shadow-soft ring-1 ring-primary"
          : campus
            ? "border-secondary/40 bg-band"
            : "border-border bg-card"
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-heading font-bold text-ink">{label}</span>
        <span className="text-muted">·</span>
        <span className="font-semibold text-ink">{session.title}</span>
        {campus && (
          <Badge variant="secondary" className="ml-auto">
            <GraduationCap className="mr-1 h-3.5 w-3.5" aria-hidden />
            Campus Time
          </Badge>
        )}
        {isNext && !campus && (
          <Badge variant="accent" className="ml-auto">
            Next session
          </Badge>
        )}
      </div>
      {session.note && <p className="mt-1 text-sm text-muted">{session.note}</p>}
    </li>
  );
}

function SessionsPage() {
  const season = useBlock("season");
  const locations = useBlock("locations");
  const banners = useBlock("banners");
  const { programs, program: currentProgram, open } = useCurrentProgram();
  const summary = summariseSeason(season.sessions);
  const groups = groupSessionsByMonth(season.sessions);

  useSeo({
    title: `${season.name} Sessions | Connect Sydney`,
    description: `Connect ${season.name}: ${summary.startLabel}, Mondays ${season.time} at ${locationNames(locations.items)}. All the dates, plus Campus Time.`,
    path: "/sessions",
    image: banners.sessionsImage,
  });

  // Programs from the host portal, minus the current season program itself —
  // otherwise "Connect 2026/2027" would list itself as an extra below its own
  // schedule.
  const extras = programs
    .filter((p) => p.active === 1 && !hasProgramFinished(p) && p.id !== currentProgram?.id)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <>
      <JsonLd data={eventsJsonLd(season, locations)} />
      <PageIntro
        eyebrow={`Connect ${season.name}`}
        title={season.headline}
        description={season.intro}
        image={banners.sessionsImage}
        imageAlt={banners.sessionsImageAlt}
        actions={
          <Button asChild variant="accent">
            <Link to="/find-us">
              <MapPin className="mr-1.5 h-4 w-4" aria-hidden />
              Find your location
            </Link>
          </Button>
        }
      />

      <Section tone="paper" spacing="lg">
        <div className="grid gap-10 lg:grid-cols-[1fr_minmax(0,22rem)]">
          <div>
            <div className="flex flex-wrap gap-3">
              <Badge variant="accent" className="px-3 py-1.5 text-sm">
                <CalendarDays className="mr-1.5 h-4 w-4" aria-hidden />
                {summary.startLabel}
              </Badge>
              <Badge variant="outline" className="px-3 py-1.5 text-sm">
                <Clock className="mr-1.5 h-4 w-4" aria-hidden />
                Mondays {season.time}
              </Badge>
              <Badge variant="outline" className="px-3 py-1.5 text-sm">
                <UtensilsCrossed className="mr-1.5 h-4 w-4" aria-hidden />
                Dinner optional
              </Badge>
            </div>

            <div className="mt-6">
              {open ? (
                <Button asChild variant="accent" size="lg">
                  <Link to="/register">
                    Register now
                    <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              ) : (
                <p className="inline-flex items-center rounded-card border border-dashed border-border bg-paper px-4 py-2 text-sm font-semibold text-muted">
                  Registrations open soon — just come along, no need to wait.
                </p>
              )}
            </div>

            <h2 className="mt-10 font-heading text-2xl font-extrabold text-ink">
              Schedule ({summary.sessionCount} Connect sessions + {summary.campusCount} Campus Time)
            </h2>
            <div className="mt-4 space-y-6">
              {groups.map((group) => (
                <div key={group.label}>
                  <h3 className="text-sm font-bold uppercase tracking-wide text-primary">{group.label}</h3>
                  <ul className="mt-2 space-y-2">
                    {group.sessions.map((s) => (
                      <SessionRow key={s.date + s.title} session={s} isNext={summary.next?.date === s.date} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="mt-6 rounded-card border border-secondary/40 bg-band p-5 leading-relaxed text-ink">
              <strong className="font-semibold">Campus Time:</strong> {season.campusTimeNote}
            </p>
            <p className="mt-4 text-sm text-muted">{season.dinnerNote}</p>

            <h2 className="mt-10 font-heading text-2xl font-extrabold text-ink">Locations</h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-3">
              {locations.items.map((l, i) => (
                <li key={i} className="rounded-card border border-border bg-card p-4">
                  <p className="flex items-baseline gap-2 text-sm font-bold uppercase tracking-wide text-primary">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs"
                    >
                      {locationShort(l)}
                    </span>
                    {l.region}
                  </p>
                  <p className="mt-1 font-semibold text-ink">{l.church}</p>
                  <p className="text-sm text-muted">{l.note}</p>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Button asChild variant="outline">
                <Link to="/find-us">Maps and directions</Link>
              </Button>
            </div>
          </div>

          {season.poster ? (
            <figure className="lg:sticky lg:top-24 lg:self-start">
              <img
                src={season.poster}
                alt={season.posterAlt}
                width={422}
                height={596}
                loading="eager"
                className="mx-auto w-full max-w-xs rounded-card shadow-card lg:max-w-none"
              />
              <figcaption className="mt-3 text-center text-xs text-muted">
                Share the poster — screenshot it or send a friend this page.
              </figcaption>
            </figure>
          ) : (
            <div className="rounded-card border border-border bg-card p-6 shadow-soft lg:sticky lg:top-24 lg:self-start">
              <h3 className="font-heading text-lg font-bold text-ink">At a glance</h3>
              <dl className="mt-4 space-y-4 text-sm">
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-primary">Dates</dt>
                  <dd className="mt-1 text-ink">
                    {summary.startLabel} – {summary.endLabel.replace(/^Ends\s+/i, "")}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-primary">Time</dt>
                  <dd className="mt-1 text-ink">Mondays {season.time}</dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-primary">Locations</dt>
                  <dd className="mt-1 text-ink">{locationShortList(locations.items)}</dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-primary">Dinner</dt>
                  <dd className="mt-1 text-ink">{season.dinnerNote}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </Section>

      {/* Programs from the host portal, when any exist besides the season itself. */}
      {extras.length > 0 && (
        <Section tone="card" spacing="lg">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-extrabold text-ink">Also on</h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {extras.map((p) => {
              const dates = formatEventDates(p.start_date, p.end_date);
              const programOpen = canEnrolNow(p);
              return (
                <Card key={p.id} className="flex h-full flex-col overflow-hidden">
                  {p.image_url && (
                    <img src={p.image_url} alt="" className="h-48 w-full object-cover" loading="lazy" />
                  )}
                  <CardHeader>
                    {programOpen && <Badge variant="accent" className="w-fit">Registrations open</Badge>}
                    <CardTitle className="font-heading text-2xl">{p.title}</CardTitle>
                    {dates && <p className="text-sm text-muted">{dates}</p>}
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col">
                    <p className="whitespace-pre-line text-sm leading-relaxed text-muted">{p.schedule}</p>
                    {p.location && <p className="mt-2 whitespace-pre-line text-sm text-muted">{p.location}</p>}
                    {programOpen && (
                      <div className="mt-5">
                        <Button asChild variant="accent" size="sm">
                          <Link to="/register" search={{ program: String(p.id) }}>
                            Register
                            <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
                          </Link>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </Section>
      )}
    </>
  );
}
